import { toast } from 'sonner';

/** User-facing copy when AI is disabled. */
export const AI_FEATURES_UNAVAILABLE_TOAST =
  'This feature is currently unavailable. Try again later. Check status.lurna.co for more information.';

/**
 * Granular AI capabilities. Toggle each via `VITE_AI_FEATURE_*` (see README in this file comments via docs).
 */
export type AiFeature =
  | 'chat'
  | 'studySets'
  | 'summary'
  | 'quiz'
  | 'notes'
  | 'courseMaterials'
  | 'autocomplete';

export const AI_FEATURES_LIST: readonly AiFeature[] = [
  'chat',
  'studySets',
  'summary',
  'quiz',
  'notes',
  'courseMaterials',
  'autocomplete',
] as const;

/** When an env flag is omitted: feature is on. */
export const DEFAULT_AI_FEATURES_ALL_ENABLED: Record<AiFeature, boolean> = {
  chat: true,
  studySets: true,
  summary: true,
  quiz: true,
  notes: true,
  courseMaterials: true,
  autocomplete: true,
};

/** Maps each capability to its Vite env key (`ImportMetaEnv`). */
export const AI_FEATURE_ENV_KEYS: Record<AiFeature, keyof ImportMetaEnv> = {
  chat: 'VITE_AI_FEATURE_CHAT',
  studySets: 'VITE_AI_FEATURE_STUDY_SETS',
  summary: 'VITE_AI_FEATURE_SUMMARY',
  quiz: 'VITE_AI_FEATURE_QUIZ',
  notes: 'VITE_AI_FEATURE_NOTES',
  courseMaterials: 'VITE_AI_FEATURE_COURSE_MATERIALS',
  autocomplete: 'VITE_AI_FEATURE_AUTOCOMPLETE',
};

function parseEnvBoolString(
  raw: string | undefined,
  defaultWhenMissing: boolean
): boolean {
  if (raw === undefined || raw === '') return defaultWhenMissing;
  const s = raw.toLowerCase().trim();
  if (s === '') return defaultWhenMissing;
  return s !== 'false' && s !== '0' && s !== 'no' && s !== 'off';
}

/** Master kill switch (`VITE_AI_MASTER_ENABLED`). Omitted ⇒ enabled. */
export function readAiFeatureFlagsFromImportMeta(
  env: ImportMetaEnv
): { masterEnabled: boolean; features: Record<AiFeature, boolean> } {
  const features: Record<AiFeature, boolean> = {
    ...DEFAULT_AI_FEATURES_ALL_ENABLED,
  };

  for (const key of AI_FEATURES_LIST) {
    const raw = env[AI_FEATURE_ENV_KEYS[key]] as string | undefined;
    if (raw !== undefined && raw !== '') {
      features[key] = parseEnvBoolString(raw, true);
    }
  }

  return {
    masterEnabled: parseEnvBoolString(env.VITE_AI_MASTER_ENABLED, true),
    features,
  };
}

export function notifyAiFeaturesUnavailable(): void {
  toast.error(AI_FEATURES_UNAVAILABLE_TOAST);
}

/** Maps AI sidebar FAB actions to feature keys. */
export function aiSidebarActionToFeature(
  action: 'flashcards' | 'summary' | 'topics' | 'chat'
): AiFeature {
  switch (action) {
    case 'flashcards':
    case 'topics':
      return 'studySets';
    case 'summary':
      return 'summary';
    case 'chat':
      return 'chat';
  }
}
